import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import MyVendorTableRequestBadge from "./MyVendorTableRequestBadge";

export const MY_VENDOR_TABLE_REQUEST_CELL_MIN_HEIGHT_PX = 88;

/**
 * Badge cell for My Requests / Library Requests table columns.
 * Collapsed: fills row height (min 2 badge rows) with overflow chevron.
 * Expanded: both cells in the row share expand state from the parent.
 */
export default function MyVendorTableRequestCell({
  items = [],
  emptyLabel,
  onClickItem,
  expanded = false,
  inverted = false,
  onToggleExpand,
}) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const wrapperEl = wrapperRef.current;
    const containerEl = containerRef.current;
    if (!wrapperEl || !containerEl) {
      setIsOverflowing(false);
      return undefined;
    }

    const measure = () => {
      if (expanded) return;
      const overflows = containerEl.scrollHeight > containerEl.clientHeight + 1;
      setIsOverflowing((prev) => (prev === overflows ? prev : overflows));
    };

    let measureRaf = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(measureRaf);
      measureRaf = requestAnimationFrame(measure);
    };

    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(scheduleMeasure);
      ro.observe(wrapperEl);
    }
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      cancelAnimationFrame(measureRaf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [items, expanded]);

  if (!items.length) {
    return <span className="text-xs text-gray-400">{emptyLabel}</span>;
  }

  return (
    <div ref={wrapperRef} className="relative h-full min-h-[88px] min-w-0 w-full">
      <div
        ref={containerRef}
        className={`flex flex-wrap items-start gap-1.5 ${
          isOverflowing ? "pr-7" : ""
        } ${expanded ? "" : "absolute inset-0 overflow-hidden"}`}
      >
        {items.map((request) => (
          <MyVendorTableRequestBadge
            key={request.id}
            request={request}
            inverted={inverted}
            onClick={onClickItem}
          />
        ))}
      </div>
      {isOverflowing ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand?.();
          }}
          className="absolute right-0 top-0 inline-flex items-center justify-center rounded-md bg-gray-50 p-1 text-gray-600 transition-colors hover:bg-gray-100"
          title={expanded ? "Collapse row" : "Expand row"}
          aria-label={expanded ? "Collapse row" : "Expand row"}
        >
          <ChevronDownIcon
            className={`h-3.5 w-3.5 transform transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </button>
      ) : null}
    </div>
  );
}
